import { useEffect, useRef, useState } from 'react';
import './GolfHud.css';

/**
 * Minigolf HUD (GameKind "golf") — the putt control + scorecard.
 *
 * The ball is a LOCAL Unity sim (FlexoGolfController): Unity streams {hole,strokes,near,
 * resting,finished} via the 'golfStatus' bridge event (forwarded as a window 'unity-event'
 * CustomEvent), and the PUTT button sends power straight into Unity via
 * window.sendUnityMessage('FlexoGolf','OnPutt','{"power":0.7}') — no server round-trip.
 * Aim by pointing the camera; hold PUTT to charge (the meter ping-pongs), release to hit.
 * The shared scorecard comes from the server game_state (GolfRules).
 */

interface GolfStatus {
  hole: number;
  maxHole: number;
  strokes: number;
  resting: boolean;
  near: boolean;
  finished: boolean;
}

interface GolfServerState {
  totals?: Record<string, number>;
  onHole?: Record<string, number>;
  holes?: number;
  roster?: { id: string; name: string }[];
}

type SendUnityMessage = (go: string, method: string, param?: string | number) => void;

export function GolfHud({ state, userId }: { state: GolfServerState; userId?: string }) {
  const [status, setStatus] = useState<GolfStatus | null>(null);
  const [charging, setCharging] = useState(false);
  const [power, setPower] = useState(0);
  const powerRef = useRef(0);
  const rafRef = useRef<number>(0);
  const dirRef = useRef(1);

  // Unity golfStatus → local HUD state.
  useEffect(() => {
    const onUnityEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ eventName: string; data: unknown }>).detail;
      if (detail?.eventName !== 'golfStatus') return;
      try {
        const s = typeof detail.data === 'string' ? JSON.parse(detail.data) : detail.data;
        setStatus(s as GolfStatus);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('unity-event', onUnityEvent);
    return () => window.removeEventListener('unity-event', onUnityEvent);
  }, []);

  // Charge meter: ping-pong 0..1 while held.
  useEffect(() => {
    if (!charging) return;
    let last = performance.now();
    const tick = (t: number) => {
      const dt = (t - last) / 1000;
      last = t;
      let p = powerRef.current + dirRef.current * dt * 1.1;
      if (p >= 1) {
        p = 1;
        dirRef.current = -1;
      } else if (p <= 0) {
        p = 0;
        dirRef.current = 1;
      }
      powerRef.current = p;
      setPower(p);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [charging]);

  const beginCharge = () => {
    powerRef.current = 0;
    dirRef.current = 1;
    setPower(0);
    setCharging(true);
  };

  const releasePutt = () => {
    if (!charging) return;
    setCharging(false);
    const send = (window as Window & { sendUnityMessage?: SendUnityMessage }).sendUnityMessage;
    send?.('FlexoGolf', 'OnPutt', JSON.stringify({ power: powerRef.current }));
  };

  const canPutt = !!status && status.near && status.resting && !status.finished;
  const scoreRows = Object.entries(state.totals ?? {});
  const nameOf = (uid: string) =>
    (state.roster ?? []).find((r) => r.id === uid)?.name || 'Player';

  return (
    <div className="golf-hud">
      <div className="golf-card">
        {status?.finished ? (
          <span className="golf-hole">🏌️ Course complete — nice round!</span>
        ) : (
          <span className="golf-hole">
            ⛳ Hole {status?.hole ?? 1}/{status?.maxHole ?? state.holes ?? 6} · stroke{' '}
            {(status?.strokes ?? 0) + 1}
          </span>
        )}
        {scoreRows.length > 0 && (
          <span className="golf-scores">
            {scoreRows.map(([uid, total]) => (
              <span key={uid} className="golf-score-chip">
                {uid === userId ? 'You' : nameOf(uid)} <b>{total}</b>
                {state.onHole?.[uid] !== undefined &&
                  (state.onHole[uid] > (state.holes ?? 6) ? ' ✅' : ` · h${state.onHole[uid]}`)}
              </span>
            ))}
          </span>
        )}
      </div>

      {!status?.finished && (
        <div className="golf-putt-wrap">
          <div className="golf-power">
            <div className="golf-power-fill" style={{ width: `${Math.round(power * 100)}%` }} />
          </div>
          <button
            type="button"
            className={`golf-putt${charging ? ' charging' : ''}`}
            disabled={!canPutt}
            onPointerDown={beginCharge}
            onPointerUp={releasePutt}
            onPointerLeave={releasePutt}
            onPointerCancel={releasePutt}
          >
            {canPutt ? (charging ? 'RELEASE!' : '⛳ HOLD TO PUTT') : status?.resting ? 'Walk to your ball' : 'Rolling…'}
          </button>
          <p className="golf-aim-hint">Aim with the camera — the ball goes where you look.</p>
        </div>
      )}
    </div>
  );
}
